'use strict';

DataQueryExecutorFactory.$inject = [
	'$q',
	'mx.SolutionBuilderAgent.PersonalizationService',
	'mx.shell.EventService',
	'$mdUtil',
	'mx.sb.Agent.DataQuery.ColumnSearchPreprocessor',
	'mx.sb.Agent.DataQuery.PagingPreprocessor',
	'mx.sb.Agent.DataQuery.SearchPreprocessor',
	'mx.sb.Agent.DataQuery.SortingPreprocessor',
	'mx.sb.Agent.DataQuery.AdvanceSearchPreprocessor',
	'mx.shell.ActionManagerService',
	'mx.sb.Agent.DataQuery.Row',
	'mx.sb.Agent.DataQuery.BaseRequester',
	'mx.sb.Agent.DataQuery.DataQueryDefinition',
];
export default function DataQueryExecutorFactory(
	$q,
	personalizationService,
	eventService,
	$mdUtil,
	ColumnSearchPreprocessor,
	PagingPreprocessor,
	SearchPreprocessor,
	SortingPreprocessor,
	AdvanceSearchPreprocessor,
	actionManagerService,
	DataQueryRow,
	BaseRequester,
	DataQueryDefinition
) {
	const preprocessorConstructors = {
		paging: PagingPreprocessor,
		sorting: SortingPreprocessor,
		search: SearchPreprocessor,
		columnSearch: ColumnSearchPreprocessor,
		advanceSearch: AdvanceSearchPreprocessor,
	};

	/**
	 * @ngdoc Class
	 * @name mx.sb.Agent.DataQuery.Executor
	 *
	 * @description
	 * Executor of DataQuery
	 *
	 */

	function DataQueryExecutor() {
		this.loading = null;
		this.$q = $q;
		this.personalizationService = personalizationService;
		this.eventService = eventService;

		this._allSelectedRows = [];
		this._selectedRowsOnPage = [];
		this.allSelectedRows = this._allSelectedRows.slice();
		this.selectedRowsOnPage = this._selectedRowsOnPage.slice();
		this.selectedEntitiesOnPage = [];
		this.allSelectedEntities = [];
		this.selectionHandlers = [];
		this.rowChangeHandlers = [];

		this.debouncedLoadItems = $mdUtil.debounce(this.loadItems.bind(this), 100);
		this.dataItems = [];
	}

	DataQueryExecutor.prototype = {
		/**
		 * @ngdoc method
		 * @name mx.sb.Agent.DataQuery.Executor.init
		 *
		 * @params
		 *
		 * @description
		 * Initialization of DataQueryExecutor
		 *
		 */
		init: async function (dataQuery, requester, options) {
			if (!(dataQuery instanceof DataQueryDefinition)) {
				throw Error('Wrong format of DataQuery');
			}

			if (!(requester instanceof BaseRequester)) {
				throw Error('Wrong format of Requester');
			}

			this._requester = requester;
			await this.setDataQuery(dataQuery);
			return this.initPreprocessors(options.preprocessors);
		},

		setDataQuery: async function (dataQuery) {
			this.dataQuery = dataQuery;
			this._requester.setDataQuery(dataQuery);
			this.allEntityTypes = await this._requester.getEntityTypes();
			this.columns = dataQuery.Columns;
			this.notPersonalizedColumns = {};
			this.columns.forEach(function (column) {
				this.notPersonalizedColumns[column.Name] = angular.copy(column);
			}, this);

			this.sortColumnsByPosition();
			//TODO: check sorting

			this.dataQuery.addFilterChangeHandler(this._onColumnFiltersChanged.bind(this));
			return this._reinitRowsWithNewColumns();
		},

		_reinitRowsWithNewColumns: function () {
			this.dataItems.map(function (row) {
				return row.initProperties(this.columns);
			}, this);
		},

		updateRequestOptions: function (options) {
			this._requester.updateOptions(options);
			return (
				typeof this._requester.exportData === 'function'
					? actionManagerService.isExportAllAvailable(this._requester.allowedTypes, this.dataQuery.SchemaClassName)
					: $q.when(false)
			).then(
				function (available) {
					this.isExportSupported = available;
				}.bind(this)
			);
		},

		initPreprocessors: function (preprocessorsConfig) {
			preprocessorsConfig = preprocessorsConfig || {};

			this.preprocessors = {};

			Object.keys(preprocessorsConfig).forEach(function (name) {
				if (!this._requester.supportedPreprocessors[name] || !(typeof preprocessorsConfig[name] === 'object' && preprocessorsConfig[name].enabled)) {
					return;
				}

				const preprocessorFactory = preprocessorConstructors[name];
				if (!preprocessorFactory) {
					mx.console.warn('unknown preprocessor: ' + name);
					return;
				}

				const preprocessor = preprocessorFactory();
				this.preprocessors[name + 'Preprocessor'] = preprocessor;
				preprocessor.init(this, preprocessorsConfig[name]);
				preprocessor.addChangedHandler(this.debouncedLoadItems);
			}, this);
		},

		initPersonalization: function (personalizationKey) {
			if (!personalizationKey) {
				return this.$q.when();
			}
			this.personalizationKey = personalizationKey;

			const preprocessorPromises = Object.keys(this.preprocessors).map(function (preprocessorKey) {
				return this.preprocessors[preprocessorKey].initPersonalization(this.personalizationKey);
			}, this);

			this.unsubscribers = this.unsubscribers || [];

			const off = this.eventService.subscribeGlobal(
				mx.SolutionBuilderAgent.Events.GridPersonalizationSaved,
				function (e, data) {
					if (data && data.contextKey === this.personalizationKey) {
						this.hasPersonalization = typeof data.personalization === 'object' && Object.keys(data.personalization).length;
					}
				}.bind(this)
			);
			this.unsubscribers.push(off);

			return this.$q.all([this.personalizationService.getGridPersonalization(this.personalizationKey)].concat(preprocessorPromises)).then(
				function (res) {
					const personalization = res[0];
					this.hasPersonalization = typeof personalization === 'object' && Object.keys(personalization).length;
					this.applyColumnPersonalization(res[0]);
				}.bind(this)
			);
		},

		onDestroy: async function () {
			if (Array.isArray(this.unsubscribers)) {
				this.unsubscribers.forEach((u) => u());
			}
		},

		applyColumnPersonalization: function (personalization) {
			this.columns.forEach(function (column) {
				if (personalization[column.Name]) {
					const columnPersonalization = personalization[column.Name];

					column.Width = typeof columnPersonalization.Width !== 'undefined' ? columnPersonalization.Width : column.Width;
					column.Position = typeof columnPersonalization.__personalizedPosition !== 'undefined' ? columnPersonalization.__personalizedPosition : column.Position; //__personalizedPosition - to support legacy code
					column.visible = typeof columnPersonalization.visible !== 'undefined' ? columnPersonalization.visible : column.visible;
				}
			});

			this.sortColumnsByPosition();
		},

		resetPersonalization: function () {
			this.personalizationService.saveGridPersonalization({}, this.personalizationKey);

			this.columns.forEach(function (column) {
				column.Width = this.notPersonalizedColumns[column.Name].Width;
				column.Position = this.notPersonalizedColumns[column.Name].Position;
				column.visible = this.notPersonalizedColumns[column.Name].visible;
			}, this);

			this.sortColumnsByPosition();

			this.dataQuery.columns = this.columns = this.columns.slice(); //TODO: to reload mx-grid.columns.!!! another way?
		},

		sortColumnsByPosition: function () {
			this.columns.sort(function (a, b) {
				return (a.Position || 0) - (b.Position || 0);
			});
		},

		changeColumnsPositions: function (positionConfig) {
			this.columns.forEach(function (column) {
				const position = parseInt(positionConfig[column.Name]);
				column.Position = !isNaN(position) && position >= 0 ? position : column.Position;
			});

			this.sortColumnsByPosition();

			if (this.personalizationKey) {
				this.personalizationService.savePersonalizedColumnPosition(
					this.columns.map(function (column) {
						return {
							name: column.Name,
						};
					}),
					this.personalizationKey
				);
			}
		},

		changeColumnSize: function (columnName, size) {
			this.columns.some(function (column) {
				if (column.Name === columnName) {
					column.Width = size;
					return true;
				}
			});
			if (this.personalizationKey) {
				this.personalizationService.savePersonalizedColumnSize({ name: columnName }, size, this.personalizationKey);
			}
		},

		changeColumnVisibility: function (columnName, visibility) {
			this.columns.some(function (column) {
				if (column.Name === columnName) {
					column.visible = visibility;
					return true;
				}
			});
			if (this.personalizationKey) {
				this.personalizationService.setColumnVisibility(this.personalizationKey, columnName, visibility);
			}
		},

		rowsEqualityFunction: function (rowA, rowB) {
			if (!rowA || !rowB) {
				return false;
			}
			return this._requester.entitiesEqualityFunction(rowA.getEntity(), rowB.getEntity());
		},

		loadItems: function () {
			this.loading = true;
			const loadItemsPromise = this._requester.load(this.preprocessors).then((res) => {
				if (this._latestLoadItemsPromise !== loadItemsPromise) {
					return this._latestLoadItemsPromise;
				}
				this.loading = false;
				this._setItems(res.dataItems, isNaN(res.totalItems) ? -1 : res.totalItems);
			});

			this._latestLoadItemsPromise = loadItemsPromise;
			return loadItemsPromise;
		},

		resetItems: function () {
			this.loading = false;
			this._setItems([], 0);
		},

		_setItems: function (dataItems, totalCount) {
			this.dataItems = dataItems;
			if (this.preprocessors.pagingPreprocessor) {
				this.preprocessors.pagingPreprocessor.totalItems = totalCount;
			}
			this.syncSelectedItems();
		},

		createEntity: function (entityType, itemType) {
			return this._requester.createEntity(entityType, itemType).then(function (entity) {
				entity.$IsNewRow = true;
				return entity;
			});
		},

		createRow: function (entity) {
			return this._requester.createRow(entity).then(function (row) {
				row.$IsNewRow = true;
				return row;
			});
		},

		addRows: function (rows) {
			if (!Array.isArray(rows)) {
				return;
			}

			const dataItems = this.dataItems.slice();
			rows.forEach(function (row) {
				dataItems.unshift(row);
			});

			if (this.preprocessors.pagingPreprocessor) {
				if (this.preprocessors.pagingPreprocessor.pageSize < dataItems.length) {
					const diff = dataItems.length - this.preprocessors.pagingPreprocessor.pageSize;
					dataItems.splice(dataItems.length - diff, diff);
				}

				if (this.preprocessors.pagingPreprocessor.totalItems >= 0) {
					this.preprocessors.pagingPreprocessor.totalItems += rows.length;
				}
			}

			this._requester.addRows(rows);
			this.dataItems = dataItems;
		},

		addEntitiesAsNewRows: async function (entities) {
			this.loading = true;
			const createdRows = await Promise.all(entities.map((entity) => this.createRow(entity)));
			this.addRows(createdRows);
			this.loading = false;
			return createdRows;
		},

		getTotalItems: function () {
			this.totalLoading = true;
			return this._requester.getTotalItems(this.preprocessors, this.dataQuery).then(
				function (totalItems) {
					this.totalLoading = false;
					if (this.preprocessors.pagingPreprocessor) {
						this.preprocessors.pagingPreprocessor.totalItems = totalItems;
					}
					return totalItems;
				}.bind(this)
			);
		},

		exportData: function () {
			this.exporting = true;
			return (this.isExportSupported ? this._requester.exportData(this.preprocessors, this.dataQuery) : this.$q.when(null)).then(
				function () {
					this.exporting = false;
				}.bind(this)
			);
		},

		syncSelectedItems: function () {
			const newSelectedRowsOnPage = [];
			this._allSelectedRows.forEach(function (selectedRow, index) {
				this.dataItems.some(function (item) {
					if (this.rowsEqualityFunction(item, selectedRow)) {
						this._allSelectedRows[index] = item;
						newSelectedRowsOnPage.push(item);
						return true;
					}
				}, this);
			}, this);
			this.changePageSelection(newSelectedRowsOnPage);
		},

		changeSelection: function (allSelectedRows) {
			this._allSelectedRows = allSelectedRows;
			this.syncSelectedItems();
		},

		changePageSelection: function (newSelectedRowsOnPage) {
			newSelectedRowsOnPage = Array.isArray(newSelectedRowsOnPage) ? newSelectedRowsOnPage : [];
			this.dataItems.forEach(function (item) {
				const selectedIndex = this._allSelectedRows.indexOf(item);

				if (newSelectedRowsOnPage.indexOf(item) >= 0 && selectedIndex < 0) {
					this._allSelectedRows.push(item);
				}

				if (newSelectedRowsOnPage.indexOf(item) < 0 && selectedIndex >= 0) {
					this._allSelectedRows.splice(selectedIndex, 1);
				}
			}, this);

			this._selectedRowsOnPage = newSelectedRowsOnPage;

			this.selectedRowsOnPage = this._selectedRowsOnPage.slice();
			this.allSelectedRows = this._allSelectedRows.slice();

			this.selectedEntitiesOnPage = this.selectedRowsOnPage.map(function (row) {
				return row.getEntity();
			});
			this.allSelectedEntities = this.allSelectedRows.map(function (row) {
				return row.getEntity();
			});
			this.allEntitiesSelected = this.allSelectedEntities.length === this.preprocessors.pagingPreprocessor?.totalItems;
			this.allEntitiesOnPageSelected = this.selectedEntitiesOnPage.length === this.dataItems.length;

			this.selectionHandlers.forEach(function (handler) {
				handler({
					allSelectedRows: this.allSelectedRows,
					selectedRowsOnPage: this.selectedRowsOnPage,
					selectedEntitiesOnPage: this.selectedEntitiesOnPage,
					allSelectedEntities: this.allSelectedEntities,
					allEntitiesSelected: this.allEntitiesSelected,
					allEntitiesOnPageSelected: this.allEntitiesOnPageSelected,
				});
			}, this);
		},

		toggleRowSelection: function (row) {
			const selectedIndex = this._allSelectedRows.indexOf(row);

			if (selectedIndex >= 0) {
				this._allSelectedRows.splice(selectedIndex, 1);
			} else {
				this._allSelectedRows.push(row);
			}

			this.syncSelectedItems();
		},

		selectRow: function (row) {
			const selectedIndex = this._allSelectedRows.indexOf(row);

			if (selectedIndex < 0) {
				this.toggleRowSelection(row);
			}
		},

		selectEntireDataset: async function () {
			this.loading = true;
			const preprocessors = Object.assign({}, this.preprocessors);
			delete preprocessors.pagingPreprocessor;

			const items = (await this._requester.load(preprocessors)).dataItems;
			this.changeSelection(items);
			this.loading = false;
		},

		resetSelection: function () {
			this.changeSelection([]);
		},

		addSelectionHandler: function (handler) {
			if (typeof handler === 'function') {
				this.selectionHandlers.push(handler);
			}
		},

		loadEntityById: function (itemId) {
			return typeof this._requester.loadEntityById === 'function' ? this._requester.loadEntityById(itemId) : $q.reject();
		},

		isEntityCorrect: function (entity) {
			return typeof this._requester.isEntityCorrect === 'function' ? this._requester.isEntityCorrect(entity) : $q.resolve(true);
		},

		setEntity: function (row, newEntity) {
			if (!(row instanceof DataQueryRow)) {
				return $q.reject('Wrong format of row');
			}

			return row.setEntity(newEntity).then(
				function () {
					if (this.dataItems.indexOf(row) >= 0) {
						this.rowChangeHandlers.forEach(function (handler) {
							handler(row);
						});
					}
				}.bind(this)
			);
		},

		addRowChangeHandler: function (handler) {
			if (typeof handler === 'function') {
				this.rowChangeHandlers.push(handler);
			}
		},

		clearAllFilters: function () {
			this.dataQuery.clearAllFilters();
		},

		_onColumnFiltersChanged: function (columns) {
			if (this.preprocessors.columnSearchPreprocessor) {
				this.preprocessors.columnSearchPreprocessor.setColumnConditions(
					columns.map((column) => ({
						field: column.Name,
						operator: column.filter.operator,
						value: column.filter.value,
					}))
				);
			}
		},
	};

	return DataQueryExecutor;
}
